import React from "react";
import { Link } from "react-router-dom";
import { Users } from "lucide-react";
import SocialUserAvatar from "@/components/avatars/SocialUserAvatar";
import OrganizationAvatar from "@/components/avatars/OrganizationAvatar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { plainExcerpt } from "@/lib/markdownPlain";
import { useSocialPaths } from "@/lib/socialPaths";

export default function SocialDmConversationList({
  conversations,
  loading,
  selectedId,
  onSelect,
  currentUserId,
  className,
  emptyMessage = "No conversations yet. Start a new message with a mutual follow.",
}) {
  const paths = useSocialPaths();

  if (loading) {
    return (
      <div className={cn("p-4 text-sm text-muted-foreground", className)}>
        Loading conversations…
      </div>
    );
  }

  if (!conversations?.length) {
    return (
      <div className={cn("px-4 py-6 text-sm text-muted-foreground", className)}>
        {emptyMessage}
      </div>
    );
  }

  return (
    <ul className={cn("flex flex-col gap-0.5 px-2 py-1", className)}>
      {conversations.map((c) => {
        const active = Number(c.conversation_id) === Number(selectedId);
        const isGroup = c.type === "group";
        const peerIsOrg = Boolean(c.peer?.is_org);
        const title = isGroup
          ? c.title || "Group"
          : c.peer?.display_name || c.peer?.username || "Direct message";
        const handle = !isGroup && c.peer?.username
          ? String(c.peer.username).replace(/^@+/, "")
          : null;
        const peerHref = handle
          ? peerIsOrg
            ? paths.org(handle)
            : paths.user(handle)
          : null;
        const last = c.last_message;
        const mine =
          last?.sender_id != null &&
          currentUserId != null &&
          String(last.sender_id) === String(currentUserId);
        const shareLabel = mine ? "You shared a post" : "Shared a post";
        const preview = last?.social_post_id
          ? last?.content
            ? plainExcerpt(last.content, 100)
            : shareLabel
          : last?.content
            ? plainExcerpt(last.content, 100)
            : "No messages yet";
        const showUnread = (c.unread_count || 0) > 0;

        return (
          <li key={c.conversation_id}>
            <button
              type="button"
              onClick={() => onSelect?.(c)}
              className={cn(
                "sm-row w-full flex items-start gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors duration-150",
                active && "sm-row-active"
              )}
            >
              <div className="relative shrink-0">
                {isGroup ? (
                  <Avatar className="h-11 w-11">
                    <AvatarFallback className="text-sm bg-[hsl(var(--sm-bubble-in))]">
                      <Users className="h-4 w-4" />
                    </AvatarFallback>
                  </Avatar>
                ) : peerIsOrg ? (
                  <OrganizationAvatar
                    org={{
                      name: c.peer?.display_name,
                      avatar_url: c.peer?.avatar_url,
                    }}
                    className="h-11 w-11"
                    fallbackClassName="text-sm font-semibold"
                  />
                ) : (
                  <SocialUserAvatar
                    user={c.peer}
                    avatarUrl={c.peer?.avatar_url}
                    displayName={c.peer?.display_name}
                    className="h-11 w-11"
                    fallbackClassName="text-sm font-semibold"
                  />
                )}
                {showUnread ? (
                  <span
                    className="sm-unread-dot absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-[hsl(var(--sm-panel))]"
                    aria-hidden
                  />
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1.5 min-w-0">
                  {peerHref ? (
                    <Link
                      to={peerHref}
                      className={cn(
                        "text-[15px] truncate hover:underline",
                        showUnread ? "font-semibold" : "font-medium"
                      )}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {title}
                    </Link>
                  ) : (
                    <span
                      className={cn(
                        "text-[15px] truncate",
                        showUnread ? "font-semibold" : "font-medium"
                      )}
                    >
                      {title}
                    </span>
                  )}
                  {handle ? (
                    <span
                      className="min-w-0 truncate text-xs italic text-muted-foreground"
                      title={`@${handle}`}
                    >
                      @{handle}
                    </span>
                  ) : null}
                </div>
                <p
                  className={cn(
                    "text-xs line-clamp-2 mt-0.5",
                    showUnread
                      ? "text-foreground/80"
                      : "text-muted-foreground"
                  )}
                >
                  {isGroup && !c.last_message
                    ? `${c.members?.length || 0} members`
                    : preview}
                </p>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
